/**
 * Tappeer Design System — Semantic Color Tokens
 *
 * Consolidated palette with fully semantic names.
 * No shade suffixes (-50/-100/-700), no raw palette colors.
 * Every token has a clear purpose, fewer tokens = fewer decisions.
 *
 * @see libs/ui-toolkit/src/tappeer/Colors.mdx for full documentation.
 */

export type HexColor = `#${string}`;
export type RgbaColor = `rgba(${string})`;
export type ColorValue = HexColor | RgbaColor;

const colors = {
  primary: {
    base: '#000000', // Main actions — primary buttons, active links, primary text
    hover: '#252528' // Hover/active state for primary actions
  },
  secondary: {
    base: '#FF49A4', // Brand CTA, focus indication, brand moments
    hover: '#FF6BB3' // Hover on secondary button
  },
  foreground: {
    base: '#000000', // Body text, values, headings — default text color
    muted: '#474747', // Labels, helper text, descriptions, placeholders
    disabled: '#9090A0', // Disabled inputs, inactive items
    white: '#FFFFFF' // Text on dark / brand backgrounds (primary btn, tooltip)
  },
  background: {
    base: '#F4F4F6', // Global page background — slightly darker than card
    muted: '#ECECEC', // Hover state, sunken sections, TabSwitcher container
    disabled: '#E8E8EC', // Disabled inputs, inactive backgrounds, Toggle off track
    input: '#FFFFFF' // Cards, inputs, modals, popovers, dropdowns
  },
  border: {
    base: '#E8E8EC', // Subtle dividers, card borders, table row separators
    input: '#DDDDE2', // Default border on inputs, dropdowns, popovers
    disabled: '#CFCFD6' // Border on focused/hovered input (mild darkening)
  },
  success: {
    bg: '#F4FDE8', // Success banners, "Approved" badge, success widget
    border: '#7CC85A', // Success toast border, status dot, checkmark icon
    text: '#3F7528', // Success badge text, positive delta (+12%)
    solid: '#6FDB35' // Tappeer green — filled success circle behind a white icon
  },
  error: {
    bg: '#FFEAF4', // Error toast, "Rejected" badge, overdue widget
    border: '#E6244A', // Invalid input border, destructive btn, X icon
    text: '#C41A3B' // Validation error text, "Delete" link, overdue date
  },
  warn: {
    bg: '#FFF1D7', // Warning banners, "Pending approval" badge
    border: '#FFA600', // Warning toast border, exclamation icon
    text: '#9A6300' // Warning badge text, validation warning helper
  },
  info: {
    bg: '#E8FBFE', // Info banners, "In progress" badge, onboarding helper
    border: '#1DD5F1', // Info toast border, "i" icon, Datepicker hover outline
    text: '#0E889E' // Info badge text, Datepicker selected day, links
  },
  accent: {
    first: '#A5F07D', // Primary Accent color for charts, graphs, data viz, highlights
    second: '#1DD5F1', // Secondary accent for charts, graphs, data viz, highlights
    third: '#9277FF', // Tertiary accent for charts, graphs, data viz, highlights
    fourth: '#FF49A4', // Quaternary accent for charts, graphs, data viz, highlights
    fifth: '#F39EE5', // Quinary accent for charts, graphs, data viz, highlights
    sixth: '#FFA600' // Senary accent for charts, graphs, data viz, highlights
  },
  overlays: {
    25: 'rgba(0,0,0,0.25)',
    50: 'rgba(0,0,0,0.50)', // Scrim behind modals, sheets, drawers
    75: 'rgba(0,0,0,0.75)'
  }
} as const;

export type Colors = typeof colors;
export type AccentColor = keyof Colors['accent'];

export default colors;

export const getAccentColor = (color: AccentColor): ColorValue => {
  return colors.accent[color];
};
